// ============================================================================
// ACADEMY STUDIO PRO (V16.1 - WINDOWS UI HIGH CONTRAST)
// ARQUIVO: src/modules/academy/admin/ContentManager.jsx
// OBJETIVO: Gestão visual com bordas definidas (Container Fechado).
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchAcademyClasses,
  createAcademyClass,
  updateAcademyClass,
  deleteAcademyClass,
  reorderAcademyClasses,
} from './ContentManagerRepository';
import { ACADEMIA_CONSTANTS } from '../../../features/academy/AcademiaConstants';
import { SaveCancelBar } from '../../../components/ui/SaveCancelBar';

// DND-KIT (Drag & Drop)
import { 
    DndContext, closestCenter, KeyboardSensor, 
    PointerSensor, useSensor, useSensors 
} from '@dnd-kit/core';
import { 
    arrayMove, SortableContext, sortableKeyboardCoordinates, 
    verticalListSortingStrategy, useSortable 
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Ícones Lucide
import { 
    Folder, Video, FileText, Plus, Trash2, Save, 
    GripVertical, Database, Scale, ShieldCheck, 
    ExternalLink, Calendar, MonitorPlay, ChevronRight,
    Search, AlertCircle, Award, CheckCircle, Info, Activity,
    Layout, Layers, MoreVertical, X, PenTool, RotateCcw
} from 'lucide-react';

// --- SUB-COMPONENTE: SMART SELECT (HÍBRIDO) ---
const SmartSelect = ({ label, value, options, onChange, placeholder }) => {
    const [isCustom, setIsCustom] = useState(false);

    useEffect(() => {
        if (value && options.length > 0) {
            const exists = options.some(opt => opt.value === value);
            if (!exists && value !== '') setIsCustom(true);
        }
    }, [value, options]);

    return (
        <div className="relative">
            <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1 flex justify-between">
                {label}
                <button 
                    onClick={() => { setIsCustom(!isCustom); if(!isCustom) onChange(''); }}
                    className="text-[9px] text-blue-600 hover:text-blue-800 underline font-bold flex items-center gap-1"
                    title={isCustom ? "Voltar para lista" : "Digitar novo valor"}
                >
                    {isCustom ? <RotateCcw size={10}/> : <Plus size={10}/>}
                    {isCustom ? 'LISTA' : 'NOVO'}
                </button>
            </label>
            
            {isCustom ? (
                <div className="relative animate-fade-in">
                    <input 
                        className="w-full p-2 border-2 border-blue-100 rounded text-xs bg-blue-50/20 focus:border-blue-500 focus:bg-white outline-none transition-all font-bold text-blue-800"
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={`Digite ${label.toLowerCase()}...`}
                        autoFocus
                    />
                    <div className="absolute right-2 top-2.5 text-blue-300 pointer-events-none">
                        <PenTool size={12}/>
                    </div>
                </div>
            ) : (
                <select 
                    className="w-full p-2 border border-gray-300 rounded text-xs bg-white focus:border-blue-500 outline-none transition-all"
                    value={value} 
                    onChange={(e) => {
                        if(e.target.value === 'CUSTOM_TRIGGER') setIsCustom(true);
                        else onChange(e.target.value);
                    }}
                >
                    {options.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                    <option value="CUSTOM_TRIGGER" className="font-bold text-blue-600 bg-blue-50">+ Adicionar Outro...</option>
                </select>
            )}
        </div>
    );
};

// --- SUB-COMPONENTE: ITEM DE LISTA ARRASTÁVEL ---
const SortableItem = ({ id, item, isSelected, onClick }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
    const style = { transform: CSS.Transform.toString(transform), transition };

    const getIcon = () => {
        if (item.type === 'QUESTION') return <Database className="w-4 h-4 text-purple-600"/>;
        if (item.type === 'MATERIAL') return <FileText className="w-4 h-4 text-red-600"/>;
        return <Video className="w-4 h-4 text-blue-600"/>;
    };

    return (
        <div 
            ref={setNodeRef} 
            style={style} 
            className={`group flex items-center gap-3 p-2 rounded-md text-xs transition-all mb-1 cursor-pointer border select-none ${
                isSelected 
                ? 'bg-blue-50 border-blue-300 text-blue-900 font-bold shadow-sm' 
                : 'bg-white border-transparent hover:bg-gray-50 text-gray-700'
            }`} 
            onClick={onClick}
        >
            <div {...attributes} {...listeners} className="cursor-grab opacity-30 group-hover:opacity-100 px-1 text-gray-400">
                <GripVertical className="w-3 h-3"/>
            </div>
            <div className="shrink-0">{getIcon()}</div>
            <div className="flex-1 min-w-0">
                <p className="truncate font-medium leading-tight">{item.title || item.enunciation?.substring(0, 40) || "Sem Título"}</p>
                <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[9px] text-gray-400 uppercase tracking-wider bg-gray-100 px-1 rounded">{item.moduleId || 'GERAL'}</span>
                    {item.status === 'PUBLISHED' && <span className="w-1.5 h-1.5 rounded-full bg-green-500" title="Publicado"></span>}
                </div>
            </div>
        </div>
    );
};

// --- COMPONENTE: EDITOR DE CONTEÚDO (PAINEL DIREITO) ---
const ContentEditor = ({ item, onSave, onCancel, onDelete }) => {
    const [form, setForm] = useState(item || {
        type: 'VIDEO', title: '', status: 'DRAFT', discipline: 'ETICA',
        year: 2026, banca: 'FGV', moduleId: 'MOD_GERAL', moduleTitle: 'Geral',
        legal_meta: { norm_type: 'LEI_FED', article: '', link: '' }
    });

    useEffect(() => { if (item) setForm(item); }, [item]);

    const handleChange = (f, v) => setForm(p => ({ ...p, [f]: v }));
    const handleNested = (p, f, v) => setForm(prev => ({ ...prev, [p]: { ...(prev[p] || {}), [f]: v } }));

    // OPÇÕES
    const subjectOpts = Object.entries(ACADEMIA_CONSTANTS.SUBJECTS).map(([k,v]) => ({ value: k, label: v.label }));
    const bankOpts = ACADEMIA_CONSTANTS.EXAM_BANKS.map(b => ({ value: b.id, label: b.label }));
    const diffOpts = Object.entries(ACADEMIA_CONSTANTS.DIFFICULTY).map(([k,v]) => ({ value: k, label: v.label }));
    const normOpts = Object.entries(ACADEMIA_CONSTANTS.NORM_TYPES).map(([k,v]) => ({ value: k, label: v.label }));

    return (
        <div className="flex flex-col h-full bg-white relative">
            
            {/* 1. HEADER (LINHA CINZA ESCURA) */}
            <div className="h-16 border-b border-gray-300 flex items-center justify-between px-8 bg-gray-50/50 shrink-0">
                <div className="flex items-center gap-3">
                    <div className={`p-2.5 rounded-xl shadow-sm ${form.type === 'QUESTION' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                        {form.type === 'QUESTION' ? <Database size={20}/> : <MonitorPlay size={20}/>}
                    </div>
                    <div>
                        <h2 className="text-base font-bold text-gray-800 leading-none">Editor de Recurso</h2>
                        <span className="text-[10px] text-gray-400 font-mono uppercase tracking-wider">{item?.id ? 'EDITANDO' : 'CRIANDO NOVO'}</span>
                    </div>
                </div>
                {item?.id && (
                    <button onClick={() => onDelete(item.id)} className="text-gray-500 hover:text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-2 text-xs font-bold border border-transparent hover:border-red-200" title="Excluir">
                        <Trash2 size={16}/> EXCLUIR
                    </button>
                )}
            </div>

            {/* 2. ÁREA DE SCROLL */}
            <div className="flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar pb-24">
                
                {/* BLOCO 1: IDENTIDADE */}
                <section className="space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Info size={16} className="text-[#C5A059]"/>
                        <h3 className="text-xs font-black text-gray-700 uppercase tracking-widest">Identidade do Conteúdo</h3>
                    </div>
                    
                    <div className="bg-white p-6 rounded-2xl border border-gray-300 shadow-sm grid grid-cols-1 md:grid-cols-12 gap-6 hover:shadow-md transition-shadow">
                        <div className="md:col-span-8">
                            <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Título / Identificador</label>
                            <input 
                                className="w-full p-3 border border-gray-300 rounded-lg text-sm font-bold text-gray-800 focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]/20 outline-none transition-all"
                                value={form.title} 
                                onChange={e => handleChange('title', e.target.value)} 
                                placeholder="Ex: Aula 01 - Princípios Fundamentais"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Tipo</label>
                            <select className="w-full p-3 border border-gray-300 rounded-lg text-xs font-bold bg-gray-50 focus:bg-white outline-none" value={form.type} onChange={e => handleChange('type', e.target.value)}>
                                <option value="VIDEO">Vídeo Aula</option>
                                <option value="MATERIAL">PDF / Texto</option>
                                <option value="QUESTION">Questão</option>
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Visibilidade</label>
                            <select 
                                className={`w-full p-3 border rounded-lg text-xs font-black uppercase outline-none ${form.status === 'PUBLISHED' ? 'border-green-300 bg-green-50 text-green-700' : 'border-orange-300 bg-orange-50 text-orange-700'}`}
                                value={form.status} 
                                onChange={e => handleChange('status', e.target.value)}
                            >
                                <option value="DRAFT">Rascunho</option>
                                <option value="PUBLISHED">Publicado</option>
                            </select>
                        </div>
                    </div>
                </section>

                {/* BLOCO 2: CLASSIFICAÇÃO */}
                <section className="space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Layers size={16} className="text-[#C5A059]"/>
                        <h3 className="text-xs font-black text-gray-700 uppercase tracking-widest">Taxonomia Inteligente</h3>
                    </div>

                    <div className="bg-white p-6 rounded-2xl border border-gray-300 shadow-sm grid grid-cols-2 md:grid-cols-4 gap-6 hover:shadow-md transition-shadow">
                        <SmartSelect label="Disciplina" value={form.discipline} options={subjectOpts} onChange={v => handleChange('discipline', v)} />
                        <SmartSelect label="Banca" value={form.banca} options={bankOpts} onChange={v => handleChange('banca', v)} />
                        
                        <div>
                            <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Ano</label>
                            <input type="number" className="w-full p-2 border border-gray-300 rounded text-xs bg-white focus:border-blue-500 outline-none" value={form.year} onChange={e => handleChange('year', e.target.value)} />
                        </div>
                        
                        <SmartSelect label="Dificuldade" value={form.difficulty} options={diffOpts} onChange={v => handleChange('difficulty', v)} />
                    </div>
                </section>

                {/* BLOCO 3: QUESTÃO */}
                {form.type === 'QUESTION' && (
                    <section className="space-y-4 animate-fade-in">
                        <div className="flex items-center gap-2 mb-2">
                            <Scale size={16} className="text-purple-600"/>
                            <h3 className="text-xs font-black text-purple-700 uppercase tracking-widest">Engenharia Reversa</h3>
                        </div>

                        <div className="bg-purple-50/50 p-6 rounded-2xl border border-purple-200 shadow-sm space-y-6">
                            <div>
                                <label className="block text-[10px] font-bold text-purple-400 uppercase mb-2">Enunciado Técnico</label>
                                <textarea 
                                    className="w-full p-4 border border-purple-200 rounded-xl text-sm min-h-[150px] font-serif leading-relaxed focus:border-purple-500 outline-none bg-white shadow-inner"
                                    value={form.enunciation} 
                                    onChange={e => handleChange('enunciation', e.target.value)} 
                                    placeholder="Cole aqui o texto da questão..."
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <SmartSelect label="Tipo de Norma" value={form.legal_meta?.norm_type} options={normOpts} onChange={v => handleNested('legal_meta', 'norm_type', v)} />
                                
                                <div>
                                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Artigo / Súmula</label>
                                    <input className="w-full p-2 border border-gray-300 rounded text-xs" value={form.legal_meta?.article} onChange={e => handleNested('legal_meta', 'article', e.target.value)} placeholder="Ex: Art. 5º, LVII" />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Link Planalto/Tribunal</label>
                                    <div className="flex gap-1">
                                        <input className="flex-1 p-2 border border-gray-300 rounded text-xs" value={form.legal_meta?.link} onChange={e => handleNested('legal_meta', 'link', e.target.value)} placeholder="https://..." />
                                        {form.legal_meta?.link && <button onClick={() => window.open(form.legal_meta.link)} className="p-2 bg-purple-100 text-purple-600 rounded hover:bg-purple-200"><ExternalLink size={14}/></button>}
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-[10px] font-bold text-gray-500 uppercase mb-2">Gabarito Comentado</label>
                                <textarea 
                                    className="w-full p-4 border border-purple-200 rounded-xl text-sm min-h-[120px] leading-relaxed focus:border-purple-500 outline-none bg-white shadow-inner"
                                    value={form.justification} 
                                    onChange={e => handleChange('justification', e.target.value)} 
                                    placeholder="Explique tecnicamente por que a resposta é essa..."
                                />
                            </div>
                        </div>
                    </section>
                )}

                {/* BLOCO 4: MÍDIA */}
                {form.type !== 'QUESTION' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fade-in">
                        <div className="bg-white p-6 rounded-2xl border border-gray-300 shadow-sm space-y-4 hover:shadow-md transition-shadow">
                            <h3 className="text-xs font-black text-gray-600 uppercase border-b border-gray-100 pb-2 mb-2 flex gap-2"><MonitorPlay size={14}/> Fonte de Mídia</h3>
                            <div>
                                <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">ID / URL</label>
                                <div className="flex gap-2">
                                    <input className="flex-1 p-2 border border-gray-300 rounded text-xs font-mono" value={form.videoId || form.link} onChange={e => handleChange(form.type === 'VIDEO' ? 'videoId' : 'link', e.target.value)} placeholder="ID Youtube ou Link PDF..." />
                                    <button className="p-2 bg-gray-100 border border-gray-300 rounded text-gray-600"><ExternalLink size={14}/></button>
                                </div>
                            </div>
                            {form.type === 'VIDEO' && (
                                <div className="grid grid-cols-2 gap-4">
                                    <div><label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Duração</label><input className="w-full p-2 border border-gray-300 rounded text-xs" value={form.duration} onChange={e => handleChange('duration', e.target.value)} placeholder="00:00" /></div>
                                    <div><label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Acesso</label><select className="w-full p-2 border border-gray-300 rounded text-xs" value={form.tier} onChange={e => handleChange('tier', e.target.value)}><option value="FREE">Gratuito</option><option value="PRO">Premium</option></select></div>
                                </div>
                            )}
                        </div>

                        <div className="bg-white p-6 rounded-2xl border border-gray-300 shadow-sm space-y-4 hover:shadow-md transition-shadow">
                            <h3 className="text-xs font-black text-gray-600 uppercase border-b border-gray-100 pb-2 mb-2 flex gap-2"><Folder size={14}/> Organização</h3>
                            <div>
                                <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Título do Módulo</label>
                                <input className="w-full p-2 border border-gray-300 rounded text-xs" value={form.moduleTitle} onChange={e => handleChange('moduleTitle', e.target.value)} placeholder="Ex: Módulo 1 - Ética" />
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">ID do Módulo (Slug)</label>
                                <input className="w-full p-2 border border-gray-300 rounded text-xs font-mono uppercase bg-gray-50 text-gray-600" value={form.moduleId} onChange={e => handleChange('moduleId', e.target.value.replace(/\s/g, '_'))} placeholder="MOD_ETICA" />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* 3. FOOTER (LINHA CINZA ESCURA) */}
            <div className="h-20 bg-white border-t border-gray-300 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] absolute bottom-0 left-0 right-0 px-8 flex items-center justify-between z-30">
                <div className="text-xs text-gray-400 font-medium">
                    {item?.id ? `ID: ${item.id}` : 'Novo Registro não salvo'}
                </div>
                <div className="flex gap-4">
                    <SaveCancelBar
                        onCancel={onCancel}
                        onSave={() => onSave(form)}
                        cancelLabel="Cancelar"
                        saveLabel="Salvar Alterações"
                    />
                </div>
            </div>

        </div>
    );
};

// --- COMPONENTE PRINCIPAL (LAYOUT GERAL) ---
const ContentManager = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isCreating, setIsCreating] = useState(false);
    const [tree, setTree] = useState({});
    const [searchTerm, setSearchTerm] = useState('');

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const data = await fetchAcademyClasses();
            setItems(data);
            rebuildTree(data);
        } catch (e) { console.error("Erro load:", e); }
        finally { setLoading(false); }
    };

    const rebuildTree = (dataList) => {
        const newTree = {};
        dataList.forEach(item => {
            const modId = item.moduleId || 'GERAL';
            if (!newTree[modId]) newTree[modId] = { title: item.moduleTitle || 'Geral', lessons: [] };
            newTree[modId].lessons.push(item);
        });
        setTree(newTree);
    };

    const handleSave = async (data) => {
        try {
            if (!data.id) {
                await createAcademyClass({ ...data, orderIndex: items.length });
            } else {
                await updateAcademyClass(data.id, data);
            }
            setIsCreating(false); setSelectedItem(null); loadData();
            alert("✅ Salvo com sucesso!");
        } catch (e) { alert("Erro ao salvar: " + e.message); }
    };

    const handleDelete = async (id) => {
        if (!confirm("Tem certeza que deseja excluir este item?")) return;
        try {
            await deleteAcademyClass(id);
            setSelectedItem(null);
            loadData();
        } catch (e) { alert("Erro ao excluir: " + e.message); }
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        if (active.id !== over.id) {
            const oldIndex = items.findIndex(i => i.id === active.id);
            const newIndex = items.findIndex(i => i.id === over.id);
            const newOrder = arrayMove(items, oldIndex, newIndex);
            setItems(newOrder);
            rebuildTree(newOrder);
            try {
                await reorderAcademyClasses(newOrder);
            } catch (e) { console.error("Erro reorder:", e); loadData(); }
        }
    };

    return (
        // AQUI ESTÁ A CORREÇÃO PRINCIPAL: BORDAS COMPLETAS NO CONTAINER RAIZ
        <div className="h-full flex flex-row overflow-hidden font-sans bg-gray-100 border border-gray-300 rounded-xl shadow-sm m-1">
            {/* SIDEBAR (ÁRVORE) - Borda Direita Escura */}
            <div className="w-80 bg-gray-50 border-r border-gray-300 flex flex-col h-full shrink-0">
                <div className="p-3 border-b border-gray-300 bg-gray-100 flex justify-between items-center shrink-0">
                    <span className="text-xs font-bold text-gray-600 uppercase tracking-wide px-2">Explorador</span>
                    <button onClick={() => { setSelectedItem(null); setIsCreating(true); }} className="bg-white border border-gray-300 hover:bg-blue-50 hover:border-blue-300 text-blue-700 p-1.5 rounded shadow-sm transition-all"><Plus size={16}/></button>
                </div>
                <div className="p-3 border-b border-gray-200 bg-white">
                    <div className="relative">
                        <Search className="absolute left-2.5 top-2 text-gray-400 w-3.5 h-3.5" />
                        <input className="w-full pl-8 pr-3 py-1.5 bg-gray-50 border border-gray-300 rounded text-xs outline-none focus:border-blue-400 focus:bg-white transition-colors" placeholder="Filtrar arquivos..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto custom-scrollbar p-2">
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        {loading ? <div className="text-center py-10 text-gray-400 text-xs">Carregando...</div> : 
                            Object.entries(tree).map(([modId, module]) => (
                                <div key={modId} className="mb-4">
                                    <div className="flex items-center gap-2 px-2 py-1.5 text-gray-500 font-bold text-xs uppercase mb-1"><Folder className="w-3.5 h-3.5 fill-yellow-400 text-yellow-600"/><span className="truncate">{module.title}</span></div>
                                    <div className="ml-2 pl-2 border-l border-gray-200 space-y-0.5">
                                        <SortableContext items={module.lessons.map(l => l.id)} strategy={verticalListSortingStrategy}>
                                            {module.lessons.filter(l => l.title?.toLowerCase().includes(searchTerm.toLowerCase())).map(lesson => (
                                                <SortableItem key={lesson.id} id={lesson.id} item={lesson} isSelected={selectedItem?.id === lesson.id} onClick={() => { setSelectedItem(lesson); setIsCreating(false); }} />
                                            ))}
                                        </SortableContext>
                                    </div>
                                </div>
                            ))
                        }
                    </DndContext>
                </div>
            </div>

            {/* ÁREA PRINCIPAL (EDITOR) */}
            <div className="flex-1 bg-white h-full overflow-hidden flex flex-col relative">
                {isCreating ? <ContentEditor onSave={handleSave} onCancel={() => setIsCreating(false)} /> : 
                 selectedItem ? <ContentEditor item={selectedItem} onSave={handleSave} onDelete={handleDelete} onCancel={() => setSelectedItem(null)} /> : 
                 (
                    <div className="h-full flex flex-col items-center justify-center bg-gray-50 text-gray-400 select-none">
                        <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4 shadow-inner"><Layout size={40} className="opacity-20"/></div>
                        <h3 className="text-lg font-bold text-gray-500">Nenhum item selecionado</h3>
                        <p className="text-sm mt-1">Selecione um arquivo ou crie um novo.</p>
                        <button onClick={() => setIsCreating(true)} className="mt-6 px-6 py-2 bg-white border border-gray-300 text-gray-600 rounded shadow-sm hover:bg-gray-50 hover:border-gray-400 transition-all text-xs font-bold uppercase">Criar Novo Recurso</button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ContentManager;